/*
 * Copyright (c) 2024, King Living
 * Password validation policy from SFCC Customer Login Settings (kl-customers).
 */

/** SFCC Customer List special-character set: $%/()[]{}=?!.,-_*|+~# */
export const SFCC_PASSWORD_SPECIAL_CHAR_PATTERN = /[$%/()[\]{}=?!.,\-_*|+~#]/g;

/**
 * Mirrors kl-customers.xml defaults when Custom SCAPI preferences are unavailable.
 * @type {{
 *   minLength: number,
 *   minSpecialChars: number,
 *   forceLetters: boolean,
 *   forceNumbers: boolean,
 *   forceMixedCase: boolean
 * }}
 */
export const DEFAULT_PASSWORD_POLICY = {
  minLength: 8,
  minSpecialChars: 1,
  forceLetters: true,
  forceNumbers: true,
  forceMixedCase: true,
};

/**
 * @param {*} value
 * @param {number} fallback
 * @returns {number}
 */
const toPositiveInt = (value, fallback) => {
  const parsed = typeof value === 'string' ? parseInt(value, 10) : value;

  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : fallback;
};

/**
 * @param {*} value
 * @param {number} fallback
 * @returns {number}
 */
const toNonNegativeInt = (value, fallback) => {
  const parsed = typeof value === 'string' ? parseInt(value, 10) : value;

  return Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : fallback;
};

/**
 * @param {*} value
 * @param {boolean} fallback
 * @returns {boolean}
 */
const toBoolean = (value, fallback) => (typeof value === 'boolean' ? value : fallback);

/**
 * @param {object|null|undefined} raw
 * @returns {typeof DEFAULT_PASSWORD_POLICY}
 */
export function normalizePasswordPolicy(raw) {
  if (!raw || typeof raw !== 'object') {
    return {...DEFAULT_PASSWORD_POLICY};
  }

  return {
    minLength: toPositiveInt(raw.minLength, DEFAULT_PASSWORD_POLICY.minLength),
    minSpecialChars: toNonNegativeInt(raw.minSpecialChars, DEFAULT_PASSWORD_POLICY.minSpecialChars),
    forceLetters: toBoolean(raw.forceLetters, DEFAULT_PASSWORD_POLICY.forceLetters),
    forceNumbers: toBoolean(raw.forceNumbers, DEFAULT_PASSWORD_POLICY.forceNumbers),
    forceMixedCase: toBoolean(raw.forceMixedCase, DEFAULT_PASSWORD_POLICY.forceMixedCase),
  };
}

/**
 * @param {string} value
 * @returns {number}
 */
export function countSfccSpecialChars(value) {
  const s = value == null ? '' : String(value);

  return (s.match(SFCC_PASSWORD_SPECIAL_CHAR_PATTERN) || []).length;
}

/**
 * @param {string} value
 * @param {typeof DEFAULT_PASSWORD_POLICY} [policy]
 * @returns {Object}
 */
export function validatePassword(value, policy = DEFAULT_PASSWORD_POLICY) {
  const s = value == null ? '' : String(value);
  const normalizedPolicy = normalizePasswordPolicy(policy);

  return {
    hasMinChars: s.length >= normalizedPolicy.minLength,
    hasLetters: /\p{L}/u.test(s),
    hasUppercase: /\p{Lu}/u.test(s),
    hasLowercase: /\p{Ll}/u.test(s),
    hasNumber: /\d/.test(s),
    hasSpecialChar: normalizedPolicy.minSpecialChars <= 0 ||
      countSfccSpecialChars(s) >= normalizedPolicy.minSpecialChars,
  };
}

/**
 * @param {string} value
 * @param {typeof DEFAULT_PASSWORD_POLICY} [policy]
 * @returns {boolean}
 */
export function isPasswordValid(value, policy = DEFAULT_PASSWORD_POLICY) {
  const normalizedPolicy = normalizePasswordPolicy(policy);
  const result = validatePassword(value, normalizedPolicy);

  if (!result.hasMinChars) {
    return false;
  }

  if (normalizedPolicy.forceNumbers && !result.hasNumber) {
    return false;
  }

  if (normalizedPolicy.minSpecialChars > 0 && !result.hasSpecialChar) {
    return false;
  }

  if (normalizedPolicy.forceMixedCase && (!result.hasUppercase || !result.hasLowercase)) {
    return false;
  }

  if (normalizedPolicy.forceLetters && !normalizedPolicy.forceMixedCase && !result.hasLetters) {
    return false;
  }

  return true;
}
